import { Component, ElementRef, OnDestroy, ViewChild, ViewEncapsulation, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { MARKED_OPTIONS, MarkdownModule, MarkedRenderer, provideMarkdown } from 'ngx-markdown';
import { Renderer as MarkedBaseRenderer } from 'marked';
import { MaterialModule } from 'src/app/material.module';
import { KbChatService } from 'src/app/services/kb-chat.service';
import { PortalPageLinksService } from 'src/app/services/portal-page-links.service';
import {
  OmniChatCitation,
  OmniChatStatusPhase,
  OmniChatTurn,
  PortalPageLink,
  autoLinkCitationTitles,
  autoLinkPageNames,
  isAccessDeniedTopic,
} from 'src/app/services/kb-chat-sse.util';

/**
 * How long an answer waits for the screen list before rendering without screen links. The list is
 * requested when the panel opens, so it is normally ready long before the first answer arrives.
 */
const PAGE_LINKS_WAIT_MS = 1500;

/**
 * Marked's default `link()` renders a bare `<a href="...">` — no `target`/`rel` — so a markdown
 * link in an answer would otherwise navigate the whole Ask panel away in the same tab instead of
 * opening the cited article. This wraps marked's own `link` renderer (same escaping/`cleanUrl`
 * behavior as the default) and only splices in `target="_blank" rel="noopener noreferrer"`.
 *
 * `MarkdownComponent` still runs the result through Angular's `DomSanitizer` (`SecurityContext.HTML`,
 * the `provideMarkdown()` default used below) before it ever reaches the DOM, so this adds no new
 * trust boundary — `target`/`rel` are both in Angular's sanitizer attribute allowlist, so they pass
 * through untouched.
 *
 * `renderer.link` is assigned as a plain `function`, not a bound/arrow one, and delegates via
 * `MarkedBaseRenderer.prototype.link.call(this, token)` rather than a closure-captured instance —
 * deliberately, because `MarkdownService.parseMarked()` never uses this renderer object directly.
 * It spreads it into a fresh `RendererObject` and re-merges it onto marked's own renderer via
 * `marked.use({ renderer })` (see `ngx-markdown/marked-renderer.d.ts` + `marked`'s `Marked.use()`),
 * which invokes our function with `this` bound to *that* merged renderer — the one whose `.parser`
 * the real `Parser` sets before rendering starts. A version bound to `renderer` at construction
 * time would carry a `this.parser` that's `undefined`, since the original instance we built here is
 * never the one marked actually renders through.
 */
function createExternalLinkRenderer(): MarkedRenderer {
  const renderer = new MarkedRenderer();
  renderer.link = function (token) {
    const html = MarkedBaseRenderer.prototype.link.call(this, token);
    return html.startsWith('<a ') ? html.replace('<a ', '<a target="_blank" rel="noopener noreferrer" ') : html;
  };
  return renderer;
}

export interface AskChatMessage {
  role: 'user' | 'assistant';
  content: string;
  citations?: OmniChatCitation[];
  isError?: boolean;
  /**
   * A polite `access_denied_topic` refusal — still a normal (non-`isError`) assistant turn, but
   * flagged so the template can give it the approved mock's distinct "warm sand" tone instead of
   * the plain bot-bubble white. Purely a style hook: it changes no branching in `send()` below.
   */
  isRefusal?: boolean;
  /**
   * The question that produced this assistant turn — carried on the message itself (rather than
   * looked up from the preceding array entry) so "report a problem" can send the exact pairing it
   * flags, even if the message list is later re-sliced/filtered.
   */
  question?: string;
  /** "Report a problem" state for this turn. Absent/'idle' → just the toggle link is shown. */
  reportStatus?: 'idle' | 'composing' | 'sending' | 'sent' | 'error';
  /** Draft text for the optional reason field, kept per-message so switching turns doesn't lose it. */
  reportReason?: string;
  /** Monotonic identity for `@for` tracking — not a timestamp, just an ever-increasing counter. */
  id: number;
}

const STATUS_KEY: Record<OmniChatStatusPhase, string> = {
  authorizing: 'AskChatAuthorizing',
  thinking: 'AskChatThinking',
};

/** Client-side copy for the CHAT-13 proxy's bare (bodyless) pre-stream status codes. */
const HTTP_STATUS_KEY: Record<string, string> = {
  http_401: 'AskChatSessionExpired',
  http_403: 'AskChatForbidden',
  http_404: 'AskChatDisabled',
  http_400: 'AskChatBadRequest',
  http_413: 'AskChatTooLong',
  http_429: 'AskChatTooManyRequests',
  http_500: 'AskChatUnavailable',
  http_502: 'AskChatUnavailable',
  http_504: 'AskChatUnavailable',
  no_stream: 'AskChatNoStream',
  stream_ended_unexpectedly: 'AskChatInterrupted',
};

/**
 * Chat tab of the Ask panel: message list, input, streaming state, citation chips.
 *
 * Kept as its own component (a standalone child of `AskPanelComponent`, mirroring how
 * `AskAiChat.tsx` is its own component in the reference client) so the existing related-articles
 * code path stays a minimal diff. Owns its own flex layout — scrollable message region over a
 * pinned input row — which is why it replaces `askPanel-body` entirely rather than living inside
 * the existing `ng-scrollbar` (nesting there would fight the outer scrollbar for the input row).
 *
 * `provideMarkdown()` is scoped to this component only: no global HttpClient-backed remote `[src]`
 * loading is needed here, so none of the 9 `app.config.ts` files need touching for it.
 */
@Component({
  selector: 'app-ask-panel-chat',
  imports: [CommonModule, FormsModule, MaterialModule, TablerIconsModule, TranslateModule, NgScrollbarModule, MarkdownModule],
  providers: [
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: () => ({ renderer: createExternalLinkRenderer() }),
      },
    }),
  ],
  templateUrl: './ask-panel-chat.component.html',
  styleUrl: './ask-panel-chat.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class AskPanelChatComponent implements OnDestroy {
  private readonly kbChatService = inject(KbChatService);
  private readonly pageLinks = inject(PortalPageLinksService);
  private readonly translate = inject(TranslateService);

  /** Drawer open/close state, passed down from `AskPanelComponent`. */
  panelOpened = input(false);

  @ViewChild('scrollAnchor') private scrollAnchor?: ElementRef<HTMLElement>;
  @ViewChild('chatInput') private chatInput?: ElementRef<HTMLTextAreaElement>;

  messages = signal<AskChatMessage[]>([]);
  loading = signal(false);
  statusPhase = signal<OmniChatStatusPhase | null>(null);
  /** Fatal, non-bubble error — a thrown failure from the generator loop itself, not an in-band `error` event. */
  fatalError = signal<string | null>(null);
  input = signal('');

  private abortController: AbortController | null = null;
  /** Monotonic counter backing `AskChatMessage.id` — plain `Date.now()` can collide under a
   *  coarsened clock (privacy-throttled timers) or a fast local stub emitting several messages
   *  within the same millisecond, which would break `@for` tracking. */
  private msgSeq = 0;
  private nextMsgId(): number {
    return ++this.msgSeq;
  }

  constructor() {
    // Panel close: AskPanelComponent (and this child) is built once and stays alive, invisible,
    // behind the closed drawer — it is not destroyed on close. Without this, closing the panel
    // mid-answer would leave a streaming fetch running against a component the user can no longer
    // see.
    effect(() => {
      if (!this.panelOpened()) {
        this.abortController?.abort();
      } else {
        // Warm the screen list used to link screen names in answers (see `linkableAnswer`).
        void this.pageLinks.load();
      }
    });

    // Scroll to the newest message/typing indicator whenever either changes.
    effect(() => {
      this.messages();
      this.loading();
      queueMicrotask(() => this.scrollAnchor?.nativeElement?.scrollIntoView({ block: 'end', behavior: 'smooth' }));
    });

    // Auto-grow the textarea with its content. Resetting to 'auto' before reading
    // `scrollHeight` lets it shrink back down as well as grow — without it, scrollHeight would
    // stay pinned to the tallest height ever reached. The SCSS `max-height` on
    // `.askPanelChat-input` (~5 lines) then clamps the assigned height, so past that point this
    // keeps growing the *content* box while the visible box stays capped and scrolls internally.
    // Depending on `input()` (rather than a template `(input)` handler) means this also fires
    // when `send()` clears the box, resetting it back to its single-row baseline.
    effect(() => {
      this.input();
      queueMicrotask(() => {
        const el = this.chatInput?.nativeElement;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      });
    });
  }

  /**
   * The answer's markdown with its links added: the first plain-text mention of each cited
   * article's title (`autoLinkCitationTitles`), and the first bold mention of each portal screen
   * the user can open (`autoLinkPageNames`). Both open in a new tab, like every chat link.
   */
  private async linkableAnswer(markdown: string, citations: OmniChatCitation[]): Promise<string> {
    const withArticles = autoLinkCitationTitles(markdown, citations);
    const timeout = new Promise<PortalPageLink[]>((resolve) => setTimeout(() => resolve([]), PAGE_LINKS_WAIT_MS));
    const pages = await Promise.race([this.pageLinks.load(), timeout]);
    // A screen name that exists in several systems (System Reports) should open the one in the
    // system the user is in, so put this system's screens first.
    const here = new URL(document.baseURI).pathname.replace(/\/+$/, '') + '/';
    const local = pages.map((p) => (p.shared ? { ...p, url: here + p.url.split('/').pop() } : p));
    const ordered = [...local.filter((p) => p.url.startsWith(here)), ...local.filter((p) => !p.url.startsWith(here))];
    return autoLinkPageNames(withArticles, ordered);
  }

  ngOnDestroy(): void {
    this.abortController?.abort();
  }

  get statusLabelKey(): string {
    const phase = this.statusPhase();
    return phase ? STATUS_KEY[phase] : 'AskChatThinking';
  }

  /** True while waiting on the network with no assistant message landed yet for the current turn. */
  get showTypingIndicator(): boolean {
    return this.loading() && this.statusPhase() !== null;
  }

  citationLabel(citation: OmniChatCitation): string {
    return citation.title || citation.slug || citation.url;
  }

  trackById(_index: number, message: AskChatMessage): number {
    return message.id;
  }

  private patchMessage(id: number, patch: Partial<AskChatMessage>): void {
    this.messages.update((current) => current.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }

  /** Opens/closes the reason composer under one answer. A no-op once that turn was already reported. */
  toggleReport(message: AskChatMessage): void {
    if (message.reportStatus === 'sent' || message.reportStatus === 'sending') return;
    const opening = message.reportStatus !== 'composing';
    this.patchMessage(message.id, { reportStatus: opening ? 'composing' : 'idle' });
  }

  setReportReason(message: AskChatMessage, value: string): void {
    this.patchMessage(message.id, { reportReason: value });
  }

  async submitReport(message: AskChatMessage): Promise<void> {
    this.patchMessage(message.id, { reportStatus: 'sending' });
    try {
      await this.kbChatService.report({
        question: message.question ?? '',
        answer: message.content,
        citations: message.citations,
        pageUrl: window.location.href,
        reason: message.reportReason?.trim() || undefined,
        lang: this.translate.currentLang || this.translate.getDefaultLang(),
      });
      this.patchMessage(message.id, { reportStatus: 'sent' });
    } catch {
      this.patchMessage(message.id, { reportStatus: 'error' });
    }
  }

  async send(): Promise<void> {
    const text = this.input().trim();
    if (!text || this.loading()) return;

    const controller = new AbortController();
    this.abortController = controller;

    const priorTurns: OmniChatTurn[] = this.messages()
      .filter((m) => !m.isError)
      .map((m) => ({ role: m.role, content: m.content }));

    this.messages.update((current) => [...current, { role: 'user', content: text, id: this.nextMsgId() }]);
    this.input.set('');
    // Collapse the auto-grown textarea back to its single-row baseline now. The input() effect
    // alone races the one-way ngModel DOM write: its microtask would measure scrollHeight while the
    // just-sent (long) text is still in the element, leaving the box stuck tall. Clearing the
    // element's value and height here makes the reset deterministic.
    const inputEl = this.chatInput?.nativeElement;
    if (inputEl) {
      inputEl.value = '';
      inputEl.style.height = 'auto';
    }
    this.fatalError.set(null);
    this.statusPhase.set('authorizing');
    this.loading.set(true);

    try {
      for await (const event of this.kbChatService.ask({ question: text, history: priorTurns, signal: controller.signal })) {
        if (event.type === 'status') {
          this.statusPhase.set(event.phase);
        } else if (event.type === 'answer') {
          const content = await this.linkableAnswer(event.markdown, event.citations);
          this.statusPhase.set(null);
          this.messages.update((current) => [
            ...current,
            {
              role: 'assistant',
              content,
              citations: event.citations,
              question: text,
              id: this.nextMsgId(),
            },
          ]);
        } else if (event.type === 'error') {
          this.statusPhase.set(null);
          if (isAccessDeniedTopic(event)) {
            // A polite role-based refusal, not a failure — render it like any other answer,
            // just tagged `isRefusal` so it can pick up its own bubble tone (see AskChatMessage).
            this.messages.update((current) => [
              ...current,
              { role: 'assistant', content: event.message, isRefusal: true, question: text, id: this.nextMsgId() },
            ]);
          } else {
            // Always a translated key, never the proxy's raw (and often untranslated/English-only)
            // event.message — the CHAT-13 proxy sends no body on pre-stream failures anyway (see
            // HTTP_STATUS_KEY's doc comment), so event.message there is already just our own
            // generic "Request failed (N)." placeholder from kb-chat.service.ts.
            const key = HTTP_STATUS_KEY[event.code] ?? 'AskChatError';
            const content = this.translate.instant(key);
            this.messages.update((current) => [...current, { role: 'assistant', content, isError: true, id: this.nextMsgId() }]);
          }
        }
        // "done" carries no payload — the loop ends naturally after it.
      }
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        // Expected: superseded by a new question, or the panel closed mid-stream.
      } else {
        this.fatalError.set(this.translate.instant('AskChatError'));
      }
    } finally {
      this.loading.set(false);
      this.statusPhase.set(null);
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void this.send();
    }
  }
}
